import { notificationsService } from "../services";
import { OfqualNotification } from "../services/domain/OfqualNotification";
import {
  all,
  call,
  put,
  select,
  delay,
  take,
  takeEvery,
  takeLatest,
  spawn,
} from "redux-saga/effects";
import { EventChannel, eventChannel } from "@redux-saga/core";
import { HubConnection, HubConnectionBuilder, JsonHubProtocol, LogLevel } from "@microsoft/signalr";
import { API_ROOT_NOTIFICATIONS } from "../config";

import { PagedResponse } from "@ofqual/portal-core/core/types";
import {
  ActionPayload,
  loadedNotifications,
  loadingNotifications,
  readNotification,
  readNotificationBulk,
  READING_NOTIFICATION,
  LOGIN_COMPLETE,
  READING_NOTIFICATION_BULK,
  updateParams,
  UPDATE_PARAMS,
} from "./actions";
import {
  authSelector,
  dataStateSelector,
  viewStateSelector,
} from "./selectors";
import { isOrphanPage } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";

// allow some time for the css fade out of a read notification to complete
const NOTIFICATION_FADE_OUT_MS = 250;

export function* listenNotifications() {
  const { token } = yield select(authSelector);
  const connection = new HubConnectionBuilder()
    .configureLogging(LogLevel.Warning)
    .withUrl(`${API_ROOT_NOTIFICATIONS}/notificationHub`, {
      accessTokenFactory: () => token,
    })
    .withHubProtocol(new JsonHubProtocol())
    .withAutomaticReconnect()
    .build();

  let attempt = 0;
  let connected = false;

  while (attempt < 10 && !connected) {
    attempt++;
    try {
      yield call(() => connection.start());
      connected = true
    } catch (err) {
      yield delay(1000 * attempt);
    }
  }

  if (connected) {
    const getEventChannel = (connection: HubConnection) => eventChannel<OfqualNotification>((emit) => {
      const handler = (data: OfqualNotification) => {
        emit(data);
      }
      connection.on("notification", handler);
      return () => { connection.off("notification") };
    });

    const channel: EventChannel<OfqualNotification> = yield call(getEventChannel, connection);
    yield takeEvery(channel, loadNotifications);
  }
}

function* watchNotificationParamUpdateSaga() {
  yield takeLatest(UPDATE_PARAMS, loadNotifications);
}

function* watchNotificationsSaga() {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  // if auth is not ready yet, wait until we get the signal that it has completed.
  const isAuthReady = !!auth && !!auth.token && auth.token.length > 0;
  if (!isAuthReady) {
    yield take(LOGIN_COMPLETE);
  }

  yield all([
    call(loadNotifications),
    spawn(listenNotifications)
  ]);
}

function* loadNotifications() {
  try {
    const { params } = yield select(dataStateSelector);

    const auth: ReturnType<typeof authSelector> = yield select(authSelector);

    yield put(loadingNotifications());
    const notifications: PagedResponse<OfqualNotification> = yield call(
      [notificationsService, notificationsService.getMy],
      params,
      { token: auth.token }
    );

    const orphanPageCheck = isOrphanPage(notifications);
    if (orphanPageCheck.isOrphan) {
      // we might have just cleared enough records to mean that the page we are on is empty
      //  so lets redirect to the last page where records exist
      yield put(
        updateParams({ page: orphanPageCheck.maxRemainingPageIndex })
      );
    } else {
      yield put(loadedNotifications(notifications));
    }
  } catch (err) {
    console.error(err);
  }
}

function* watchReadingNotificationSaga() {
  yield takeEvery(READING_NOTIFICATION, readingNotificationSaga);
}

function* readingNotificationSaga({ payload }: ActionPayload<string>) {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);

  // We do an 'all' with a delay to make sure that the reading of a notifaction
  //  takes *at least* NOTIFICATION_FADE_OUT_MS.  We may as well have the api call going in
  //  parrallel with the delay.  If the delay finishes first, we have a long running api call
  //  so it was a good thing we weren't doing it sequentially after the delay had finished.
  //  If the api call finishes first, it was super quick and so we still need to wait for the
  //  delay to finish to give the UI time to fade out.
  yield all({
    artificialDelay: delay(NOTIFICATION_FADE_OUT_MS), //todo: could the delay length be passed in the payload from the UI?
    actualCall: call(
      [notificationsService, notificationsService.readNotification],
      payload,
      {
        token: auth.token,
      }
    ),
  });

  yield put(readNotification(payload));
  yield call(loadNotifications);
}

function* watchReadingNotificationBulkSaga() {
  yield takeEvery(READING_NOTIFICATION_BULK, readingNotificationBulk);
}

function* readingNotificationBulk() {
  const auth: ReturnType<typeof authSelector> = yield select(authSelector);
  const { notificationsToClear } = yield select(viewStateSelector);

  yield call(
    [notificationsService, notificationsService.bulkReadNotifications],
    notificationsToClear,
    {
      token: auth.token,
    }
  );

  yield put(readNotificationBulk());
  yield call(loadNotifications);
}

export function* rootSaga() {
  yield all([
    watchNotificationsSaga(),
    watchNotificationParamUpdateSaga(),
    watchReadingNotificationSaga(),
    watchReadingNotificationBulkSaga(),
    // further sagas...
  ]);
}
